// Informes del módulo — pestañas:
//   · Paros:      GET /informes/paros (agregado por causal madre y por línea,
//                 contra la tabla REAL registros_paro de producción)
//   · Resumen OT: GET /informes/ot-resumen (distribución por estado y tipo)
//   · SLA:        GET /informes/sla (MTTR global de correctivas cerradas —
//                 el contrato del batch 1 no desagrega por activo)

import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Tab, Tabs } from '../../components/ui/tabs';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { useEstructuraMtto } from './estructura';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import { useInformeOtResumen, useInformeParos, useInformeSla } from './queries';
import {
  ESTADO_OT_CHART_COLOR,
  ESTADO_OT_LABEL,
  fmtNum,
  hoyIso,
  tipoMantenimientoLabel,
} from './shared';

const TOOLTIP_STYLE = {
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--color-fg)',
} as const;

function hace28DiasIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 28);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function InformesMttoPage() {
  const [tab, setTab] = useState('paros');

  return (
    <MantenimientoShell
      titulo="Informes"
      subtitulo="Paros de producción, distribución de órdenes y tiempos de reparación"
    >
      <Tabs value={tab} onChange={setTab}>
        <Tab value="paros">Paros</Tab>
        <Tab value="resumen">Resumen OT</Tab>
        <Tab value="sla">SLA</Tab>
      </Tabs>
      {tab === 'paros' ? <InformeParosTab /> : null}
      {tab === 'resumen' ? <InformeResumenOtTab /> : null}
      {tab === 'sla' ? <InformeSlaTab /> : null}
    </MantenimientoShell>
  );
}

// =============================================================================
// Paros
// =============================================================================

function InformeParosTab() {
  const [desde, setDesde] = useState(hace28DiasIso());
  const [hasta, setHasta] = useState(hoyIso());
  const [plantaId, setPlantaId] = useState('');
  const estructura = useEstructuraMtto();

  const rangoValido = desde <= hasta;
  const { data, isLoading, isError, error } = useInformeParos(
    rangoValido
      ? { desde, hasta, ...(plantaId ? { planta_id: plantaId } : {}) }
      : { desde: hasta, hasta: desde, ...(plantaId ? { planta_id: plantaId } : {}) },
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 pt-4 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="paros-desde">Desde</Label>
            <Input
              id="paros-desde"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="paros-hasta">Hasta</Label>
            <Input
              id="paros-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="paros-planta">Planta</Label>
            <Select
              id="paros-planta"
              value={plantaId}
              onChange={(e) => setPlantaId(e.target.value)}
              disabled={!estructura.disponible}
            >
              <option value="">Todas</option>
              {estructura.plantas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? <CargandoModulo mensaje="Cargando informe de paros…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}

      {data ? (
        <>
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 p-5 pt-5 sm:grid-cols-2">
              <KpiCard
                label="Paros registrados"
                value={fmtNum(data.total_paros)}
                sub={`${data.desde} → ${data.hasta}`}
              />
              <KpiCard
                label="Minutos perdidos"
                value={fmtNum(data.total_minutos)}
                sub={`≈ ${fmtNum(data.total_minutos / 60, 1)} horas`}
                divider
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Por causal madre</CardTitle>
              </CardHeader>
              <CardContent>
                {data.por_causal.length === 0 ? (
                  <p className="py-8 text-center text-sm text-fg-tertiary">
                    Sin paros en el período seleccionado.
                  </p>
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH>Causal madre</TH>
                        <TH align="right">Paros</TH>
                        <TH align="right">Minutos</TH>
                        <TH align="right">% minutos</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {data.por_causal.map((c) => (
                        <TR key={c.causal_madre_id}>
                          <TD className="text-sm text-fg">{c.causal_madre_nombre}</TD>
                          <TD align="right" className="text-sm tabular-nums text-fg-secondary">
                            {fmtNum(c.paros)}
                          </TD>
                          <TD align="right" className="text-sm tabular-nums text-fg">
                            {fmtNum(c.minutos)}
                          </TD>
                          <TD align="right" className="text-xs tabular-nums text-fg-tertiary">
                            {data.total_minutos > 0
                              ? `${Math.round((c.minutos / data.total_minutos) * 100)}%`
                              : '—'}
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Minutos perdidos por línea</CardTitle>
              </CardHeader>
              <CardContent>
                {data.por_linea.length === 0 ? (
                  <p className="py-8 text-center text-sm text-fg-tertiary">
                    Sin paros en el período seleccionado.
                  </p>
                ) : (
                  <div className="h-[280px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data.por_linea}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="var(--color-border)"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="linea_nombre"
                          width={110}
                          tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          cursor={{ fill: 'var(--color-surface-overlay)' }}
                          formatter={(value) => [`${fmtNum(value as number)} min`, 'Minutos']}
                        />
                        <Bar
                          dataKey="minutos"
                          fill="var(--color-warn)"
                          radius={[0, 3, 3, 0]}
                          maxBarSize={22}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

// =============================================================================
// Resumen OT
// =============================================================================

function InformeResumenOtTab() {
  const { data, isLoading, isError, error } = useInformeOtResumen();

  if (isLoading) return <CargandoModulo mensaje="Cargando resumen de órdenes…" />;
  if (isError || !data) return <ErrorModulo error={error} />;

  const porEstado = data.por_estado.map((e) => ({
    ...e,
    label: ESTADO_OT_LABEL[e.estado],
    color: ESTADO_OT_CHART_COLOR[e.estado],
  }));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-5 pt-5 sm:grid-cols-2">
          <KpiCard label="Órdenes totales" value={fmtNum(data.total)} sub="Histórico del módulo" />
          <KpiCard
            label="Proporción preventiva"
            value={fmtNum(data.proporcion_prevencion * 100, 1)}
            valueSuffix="%"
            sub="OT preventivas sobre el total"
            subTone={data.proporcion_prevencion >= 0.5 ? 'ok' : 'warn'}
            divider
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Por estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porEstado} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                    axisLine={{ stroke: 'var(--color-border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: 'var(--color-surface-overlay)' }}
                    formatter={(value) => [fmtNum(value as number), 'Órdenes']}
                  />
                  <Bar dataKey="cantidad" radius={[3, 3, 0, 0]} maxBarSize={48}>
                    {porEstado.map((e) => (
                      <Cell key={e.estado} fill={e.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Por tipo de mantenimiento</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <THead>
                <TR>
                  <TH>Tipo</TH>
                  <TH align="right">Órdenes</TH>
                  <TH align="right">% del total</TH>
                </TR>
              </THead>
              <TBody>
                {data.por_tipo.map((t) => (
                  <TR key={t.tipo_mantenimiento}>
                    <TD className="text-sm text-fg">
                      {tipoMantenimientoLabel(t.tipo_mantenimiento)}
                    </TD>
                    <TD align="right" className="text-sm tabular-nums text-fg">
                      {fmtNum(t.cantidad)}
                    </TD>
                    <TD align="right" className="text-xs tabular-nums text-fg-tertiary">
                      {data.total > 0 ? `${Math.round((t.cantidad / data.total) * 100)}%` : '—'}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// SLA (MTTR)
// =============================================================================

function InformeSlaTab() {
  const { data, isLoading, isError, error } = useInformeSla();

  if (isLoading) return <CargandoModulo mensaje="Cargando SLA…" />;
  if (isError || !data) return <ErrorModulo error={error} />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-5 pt-5 sm:grid-cols-2">
          <KpiCard
            label="MTTR"
            value={fmtNum(data.mttr_horas, 2)}
            valueSuffix="h"
            hint="Tiempo medio de reparación: promedio de horas entre la apertura y el cierre de las órdenes correctivas cerradas."
            sub="Apertura → cierre de correctivas"
          />
          <KpiCard
            label="Muestras"
            value={fmtNum(data.muestras)}
            sub="Órdenes correctivas cerradas"
            divider
          />
        </CardContent>
      </Card>
      {data.muestras === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Aún no hay órdenes correctivas cerradas — el MTTR se calcula cuando se cierre la
            primera.
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
